import { CHAIN_IDS_SUPPORT_INTENTS } from '@/constants/chain';

import { TokenResult } from './0x/token-registry.types';
import { isWrap } from './swap';

interface Eip1559Fees {
  maxPriorityFeePerGas: bigint;
  maxFeePerGas: bigint;
  baseFeePerGas: bigint;
}
interface Eip1559FeesRaw {
  maxPriorityFeePerGas: string;
  maxFeePerGas: string;
  baseFeePerGas: string;
}

interface GasInfoTypeLegacy {
  suggested: bigint;
  custom: bigint;
  type: 'legacy';
}

interface GasInfoTypeLegacyRaw {
  suggested: string;
  custom: string;
  type: 'legacy';
}

interface GasInfoTypeEIP1559 {
  suggested: Eip1559Fees;
  custom: Eip1559Fees;
  type: 'eip1559';
}

interface GasInfoTypeEIP1559Raw {
  suggested: Eip1559FeesRaw;
  custom: Eip1559FeesRaw;
  type: 'eip1559';
}

export type GasInfo = GasInfoTypeLegacy | GasInfoTypeEIP1559;

export type GasInfoRaw = GasInfoTypeLegacyRaw | GasInfoTypeEIP1559Raw;

/**
 * Check whether the Intents (Matcha Auto) lane is available for a trade by
 * referencing the sell/buy token info. Excludes cross-chain pairs, native sells,
 * wrap/unwrap, and requires the chain to support the Intents lane.
 * @param buyToken - TokenResult (optional) - Used to check the 'type'.
 * @param sellToken - TokenResult (optional) - Used to check the 'type'.
 * @returns boolean - Is the Intents lane supported for this chain and/or sell token.
 */
export function getIsIntentsSupported(
  buyToken: TokenResult | undefined,
  sellToken: TokenResult | undefined,
): boolean {
  const isSameChain = !buyToken || !sellToken || sellToken.chainId === buyToken.chainId;
  const assetIsNotNative = sellToken?.type !== 'Native';
  const chainSupportsIntents = !!sellToken && CHAIN_IDS_SUPPORT_INTENTS.includes(sellToken.chainId);
  const isWrapOrUnwrap =
    buyToken && sellToken ? isWrap(sellToken.address, buyToken.address, sellToken.chainId) : false;

  return isSameChain && !isWrapOrUnwrap && assetIsNotNative && chainSupportsIntents;
}

/**
 * Deserialize the gas oracle response.
 * @param response - The gas oracle response.
 * @returns The gas info.
 */
export function deserializeGasOracleResponse(response: GasInfoRaw): GasInfo {
  const { custom, suggested, type } = response;

  if (type === 'legacy') {
    return {
      type: 'legacy',
      custom: BigInt(custom),
      suggested: BigInt(suggested),
    };
  }

  return {
    type,
    suggested: {
      maxFeePerGas: BigInt(suggested.maxFeePerGas),
      baseFeePerGas: BigInt(suggested.baseFeePerGas),
      maxPriorityFeePerGas: BigInt(suggested.maxPriorityFeePerGas),
    },
    custom: {
      maxFeePerGas: BigInt(custom.maxFeePerGas),
      baseFeePerGas: BigInt(custom.baseFeePerGas),
      maxPriorityFeePerGas: BigInt(custom.maxPriorityFeePerGas),
    },
  };
}
